import { useState } from "react";
import { PushPin, Trash, Plus, Clock, CalendarBlank } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

// Format an ISO timestamp into a friendly local string like
// "Mon May 19 · 8:00 AM". Returns "" for falsy input.
function fmtSchedule(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Convert a Date to a value suitable for <input type="datetime-local">
// (i.e., "YYYY-MM-DDTHH:mm" in local time).
function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultScheduleValue() {
  // Default to tomorrow at 8 AM local.
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return toLocalInput(d);
}

export default function Announcements({ items, isTeacher, onCreate, onDelete, title = "Pinboard", icon: Icon = PushPin, iconClass = "text-yellow-500", emptyHint = "Pin one for the class!" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [mode, setMode] = useState("now"); // "now" | "later"
  const [scheduleLocal, setScheduleLocal] = useState(defaultScheduleValue);

  const submit = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg) return;
    let publish_at = null;
    if (mode === "later") {
      const d = new Date(scheduleLocal);
      if (isNaN(d)) return;
      // Reject "schedule in the past" client-side — backend stores as-is so
      // a past timestamp would just publish immediately, but that's confusing
      // UX so we surface it here.
      if (d.getTime() <= Date.now()) {
        // Fall through as immediate post.
        publish_at = null;
      } else {
        publish_at = d.toISOString();
      }
    }
    await onCreate(msg, publish_at);
    setText("");
    setMode("now");
    setScheduleLocal(defaultScheduleValue());
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm h-full" data-testid="announcements-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon weight="fill" className={iconClass} size={20} />
          <h3 className="font-outfit font-bold text-xl">{title}</h3>
        </div>
        {isTeacher && (
          <button
            onClick={() => setOpen(!open)}
            data-testid="announcement-toggle-btn"
            className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus size={14} weight="bold" /> New
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={submit}
            className="mb-4 overflow-hidden"
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="announcement-input"
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder="Bring your laptop on Friday..."
            />

            {/* Schedule controls */}
            <div className="mt-3 space-y-2">
              <div className="inline-flex p-0.5 rounded-lg bg-slate-100 text-xs">
                <button
                  type="button"
                  onClick={() => setMode("now")}
                  data-testid="announcement-mode-now"
                  className={`px-3 py-1 rounded-md font-semibold transition ${
                    mode === "now" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Post now
                </button>
                <button
                  type="button"
                  onClick={() => setMode("later")}
                  data-testid="announcement-mode-later"
                  className={`px-3 py-1 rounded-md font-semibold inline-flex items-center gap-1 transition ${
                    mode === "later" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <CalendarBlank size={12} weight="bold" /> Schedule
                </button>
              </div>

              {mode === "later" && (
                <div>
                  <input
                    type="datetime-local"
                    value={scheduleLocal}
                    onChange={(e) => setScheduleLocal(e.target.value)}
                    data-testid="announcement-schedule-input"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    min={toLocalInput(new Date())}
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Hidden from students and parents until this time.
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-lg hover:bg-slate-100">
                Cancel
              </button>
              <button
                type="submit"
                data-testid="announcement-submit-btn"
                className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-yellow-400 text-yellow-950 hover:bg-yellow-500"
              >
                {mode === "later" ? "Schedule" : "Post"}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No notes yet. {isTeacher && emptyHint}</div>
      ) : (
        <div className="space-y-3">
          {items.map((a, idx) => {
            const scheduled = !!a.scheduled;
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.04 }}
                className={`relative rounded-2xl p-4 pr-9 ${
                  scheduled
                    ? "bg-slate-50 border-2 border-dashed border-slate-300"
                    : "bg-yellow-100"
                }`}
                data-testid={`announcement-item-${a.id}`}
                style={{
                  transform: scheduled ? "none" : `rotate(${idx % 2 === 0 ? -0.6 : 0.5}deg)`,
                }}
              >
                {scheduled && (
                  <div
                    className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase bg-slate-900 text-white px-2 py-0.5 rounded mb-2"
                    data-testid={`announcement-scheduled-badge-${a.id}`}
                  >
                    <Clock size={12} weight="bold" /> Scheduled · {fmtSchedule(a.publish_at)}
                  </div>
                )}
                <p
                  className={`font-caveat text-2xl leading-tight ${
                    scheduled ? "text-slate-700" : "text-yellow-950"
                  }`}
                >
                  {a.message}
                </p>
                <div className={`text-[11px] mt-1 ${scheduled ? "text-slate-500" : "text-yellow-900/70"}`}>
                  — {a.author_name}
                </div>
                {isTeacher && (
                  <button
                    onClick={() => onDelete(a.id)}
                    data-testid={`announcement-delete-${a.id}`}
                    className={`absolute top-2 right-2 p-1 rounded-md ${
                      scheduled
                        ? "hover:bg-slate-200 text-slate-500"
                        : "hover:bg-yellow-200/80 text-yellow-900"
                    }`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
